import { Text } from "~/components/Text"
import { useCallback, useEffect, useState } from "react"
import { CloseCircleFilled } from "@ant-design/icons"
import { Button, Flex, Form, Input, Modal, Typography, message } from "antd"
import { useCaptcha } from "~/common/auth/captcha"
import { useCountDown } from "~/common/hooks"
import { getStoredSpm } from "~/common/analytics/spm"
import { useSessionStore } from "~/store/session"
import { MOBILE_PHONE_PATTERN } from "~/common/constants"
import styles from "./index.css"

const { Title } = Typography

type LoginFormValues = {
  code: string
  mobile: string
}

const loginCoverUrl =
  "https://cdn-testing.duobaogongxiao.com/kada-uploads/1788248983346-3nt40uxk-Frame_32.png"

const mobileRules = [
  { required: true, message: "手机号不能为空" },
  {
    pattern: MOBILE_PHONE_PATTERN,
    message: "手机号格式错误",
  },
]

const codeRules = [
  { required: true, message: "验证码不能为空" },
  {
    len: 4,
    message: "验证码错误",
  },
]

export const LoginModal = () => {
  const [form] = Form.useForm<LoginFormValues>()
  const closeLoginModal = useSessionStore((state) => state.closeLoginModal)
  const login = useSessionStore((state) => state.login)
  const loginModalOpen = useSessionStore((state) => state.loginModalOpen)
  const sendLoginCode = useSessionStore((state) => state.sendLoginCode)
  const [smsButtonDisabled, setSmsButtonDisabled] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [sendingCode, setSendingCode] = useState(false)
  const { countdown, start: startCountdown } = useCountDown()
  const mobile = Form.useWatch("mobile", form)

  const setCodeFieldError = useCallback(
    (error: unknown) => {
      form.setFields([
        {
          errors: [error instanceof Error ? error.message : String(error)],
          name: "code",
        },
      ])
    },
    [form],
  )

  const sendLoginCodeWithCaptcha = useCallback(
    async (captcha: string) => {
      setSendingCode(true)

      try {
        await sendLoginCode({
          captcha,
          mobile: form.getFieldValue("mobile"),
          verify_type: 1,
        })
        startCountdown(60)
      } catch (error) {
        setCodeFieldError(error)
      } finally {
        setSendingCode(false)
      }
    },
    [form, sendLoginCode, setCodeFieldError],
  )

  const handleCaptchaFail = useCallback(() => {
    setSendingCode(false)
    void message.error("滑块验证码加载失败，请稍后重试")
  }, [])

  const { triggerCaptcha } = useCaptcha(
    sendLoginCodeWithCaptcha,
    handleCaptchaFail,
  )

  const handleSendSmsCode = useCallback(async () => {
    await form.validateFields(["mobile"])
    triggerCaptcha()
  }, [form, triggerCaptcha])

  const handleSubmit = useCallback(async () => {
    setSubmitting(true)

    try {
      const values = await form.validateFields()
      const source = getStoredSpm()

      await login({
        code: values.code,
        mobile: values.mobile,
        ...(source ? { source } : {}),
        verify_type: 1,
      })
      form.resetFields()
      closeLoginModal()
    } catch (error) {
      setCodeFieldError(error)
    } finally {
      setSubmitting(false)
    }
  }, [closeLoginModal, form, login, setCodeFieldError])

  useEffect(() => {
    form
      .validateFields(["mobile"], { validateOnly: true })
      .then((values) => {
        setSmsButtonDisabled(!values?.mobile)
      })
      .catch(() => {
        setSmsButtonDisabled(true)
      })
  }, [form, mobile])

  return (
    <Modal
      centered
      closeIcon={<CloseCircleFilled className={styles.closeIcon} />}
      footer={null}
      onCancel={closeLoginModal}
      open={loginModalOpen}
      rootClassName={styles.modal}
      width={780}
    >
      <Flex>
        <Flex className={styles.left}>
          <img
            width="100%"
            src={loginCoverUrl}
            alt=""
          />
        </Flex>
        <Flex
          vertical
          align="center"
          className={styles.right}
        >
          <Form
            className={styles.form}
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
          >
            <Title
              className={styles.title}
              level={2}
            >
              欢迎使用 AI 视频工作台
            </Title>
            <Form.Item
              name="mobile"
              rules={mobileRules}
              className={styles.formItem}
            >
              <Input
                allowClear
                className={styles.mobileInput}
                placeholder="请输入手机号"
                size="large"
              />
            </Form.Item>
            <Form.Item
              name="code"
              rules={codeRules}
              className={styles.formItem}
            >
              <Input
                className={styles.codeInput}
                placeholder="请输入短信验证码"
                size="large"
                suffix={
                  countdown > 0 ? (
                    <Text style={{ color: "var(--ant-color-primary)" }}>
                      {countdown}s
                    </Text>
                  ) : (
                    <Button
                      variant="text"
                      color="primary"
                      disabled={smsButtonDisabled || sendingCode}
                      onClick={() => {
                        if (smsButtonDisabled || sendingCode) return
                        void handleSendSmsCode()
                      }}
                    >
                      {sendingCode ? "发送中" : "获取验证码"}
                    </Button>
                  )
                }
              />
            </Form.Item>
            <Form.Item className={styles.submitItem}>
              <Button
                block
                htmlType="submit"
                loading={submitting}
                size="large"
                type="primary"
              >
                登录
              </Button>
            </Form.Item>
            <Flex justify="center">
              <Text
                size="s"
                type="secondary"
              >
                未注册的手机号登录成功后将自动注册
              </Text>
            </Flex>
          </Form>
        </Flex>
      </Flex>
    </Modal>
  )
}
